import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import Feather from "@expo/vector-icons/Feather";
import {
  Entypo,
  Foundation,
  Ionicons,
  MaterialCommunityIcons,
} from "@expo/vector-icons";

import { RecommendationCategory } from "@/schemas/user/explore";
import {
  EXPLORE_CATEGORY_ICONS,
  IconConfig,
  getCategoryDisplayName,
} from "@/utils/explore";
import { themes } from "@/Components/Styling/themeConfigs";
import { Spacings } from "@/styling/spacings";

type Props = {
  category: RecommendationCategory;
  showSeeAllButton?: boolean;
  isLoading?: boolean;
  onSeeAllPress: () => void;
};

const FeedScreenSectionHeader = ({
  category,
  isLoading = false,
  showSeeAllButton = true,
  onSeeAllPress,
}: Props): React.JSX.Element => {
  const iconConfig: IconConfig = EXPLORE_CATEGORY_ICONS[category];
  const iconSize =
    currentTheme.typography.h1.size.mobile * (iconConfig.sizeMultiplier || 1);

  return (
    <View style={styles.container}>
      {/* icon and category name */}
      <View style={styles.leftContainer}>
        {/* icon */}
        <View style={styles.iconContainer}>
          {iconConfig.iconFamily === "Ionicons" && (
            <Ionicons
              name={iconConfig.name as keyof typeof Ionicons.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "MaterialCommunityIcons" && (
            <MaterialCommunityIcons
              name={
                iconConfig.name as keyof typeof MaterialCommunityIcons.glyphMap
              }
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "Entypo" && ( // eslint-disable-line @cspell/spellchecker
            <Entypo
              name={iconConfig.name as keyof typeof Entypo.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "Foundation" && ( // eslint-disable-line @cspell/spellchecker
            <Foundation
              name={iconConfig.name as keyof typeof Foundation.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
        </View>
        {/* category name */}
        <Text style={styles.headerText}>
          {getCategoryDisplayName(category)}
        </Text>
      </View>

      {/* see all button */}
      {showSeeAllButton && (
        <Pressable
          onPress={onSeeAllPress}
          disabled={isLoading}
          accessibilityRole="button"
          accessibilityLabel={`See all ${category} matches`}
          hitSlop={{
            top: Spacings.s2,
            bottom: Spacings.s2,
            left: Spacings.s4,
            right: Spacings.s2,
          }}
        >
          <View
            style={[
              styles.rightContainer,
              styles.semiTransparentBackgroundBorder,
            ]}
          >
            <Text style={[styles.seeAllText, isLoading && { opacity: 0.5 }]}>
              {"See all "}
            </Text>
            <Feather
              name="arrow-right"
              color="black"
              style={[styles.seeAllText, isLoading && { opacity: 0.5 }]}
            />
          </View>
        </Pressable>
      )}
    </View>
  );
};

const currentTheme = themes.morning;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: Spacings.s5,
    marginBottom: Spacings.s2,
    paddingHorizontal: Spacings.s2,
  },
  leftContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  iconContainer: {
    width: 48,
    height: 48,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 30,
    padding: Spacings.s3,
    marginRight: Spacings.s2,
    backgroundColor: "#fee140",
  },
  headerText: {
    fontSize: currentTheme.typography.h2.size.mobile,
    fontWeight: "bold",
  },
  rightContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  semiTransparentBackgroundBorder: {
    paddingHorizontal: Spacings.s3,
    paddingVertical: Spacings.s2,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: 30,
  },
  seeAllText: {
    fontSize: currentTheme.typography.h3.size.mobile + 1,
    fontWeight: "bold",
  },
});

export default FeedScreenSectionHeader;
